// src/pages/usa/colorado/ColoradoSprings.js
import "../../../App.css";
import Header from "../../../components/Header";
import Footer from "../../../components/Footer";

import BackOnly from "../../../components/BackOnly";
import ContactPopup from "../../../components/ContactPopup";
import React, { useState, useEffect } from "react";
import AffiliateBooking from "../../../components/affiliate/AffiliateBooking";
import AffiliateExpedia from "../../../components/affiliate/AffiliateExpedia";
import AffiliateTrip from "../../../components/affiliate/AffiliateTrip";

import { useNavigate } from "react-router-dom";   // ⭐ REQUIRED

// ⭐ STRICT COMMAND: Colorado Springs ALWAYS goes back to Colorado map
export const backRoute = "/map/colorado"; 
// or "/travel/usa/colorado" ONLY if that is your actual Colorado map route

export default function ColoradoSprings() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // ⭐ Send strict back command to BackOnly (NO LOOPS)
  useEffect(() => {
    if (!window.__coloradoSpringsBackRouteSet) {
      window.__coloradoSpringsBackRouteSet = true;
      navigate(".", { replace: true, state: { backRoute } });
    }
  }, [navigate]);

  return (

    <>
      <Header />

      <div className="visited-back-wrapper">
        <BackOnly />
      </div>


      <div className="page-container" style={{ padding: "12px" }}>
        <h1>Colorado Springs, Colorado</h1>

        <section className="travel-review">
          {/* LEFT COLUMN — TEXT */}
          <div className="travel-text">
            <h2>🏞️ Discover Colorado Springs, Colorado</h2>

            <p>
              Colorado Springs is a city rich in natural beauty, outdoor adventure, and cultural charm.
              Sitting at an elevation of <strong>6,035 feet</strong>, it offers stunning views and easy access to
              iconic landmarks like <strong>Pikes Peak</strong> and <strong>Garden of the Gods</strong>. The city is
              served by <strong>Colorado Springs Airport (COS)</strong>, just minutes from downtown, with broader
              flight options available at <strong>Denver International Airport (DEN)</strong>, about 90 minutes north.
            </p>

            <h3>🚗 Getting There</h3>

            <p>
              The drive from Denver to Colorado Springs takes about 1.5 hours, depending on traffic and weather.
              Traveling south on I‑25, you'll pass open plains and dramatic views of the Front Range, with Pikes Peak
              rising in the distance as you approach the city.
            </p>

            <p>
              For air travel, you have two solid options: <strong>Denver International Airport (DEN)</strong> and the
              more convenient <strong>Colorado Springs Airport (COS)</strong>, just 20 minutes from downtown. Whether
              you're flying in or driving down, it's a smooth and scenic journey — perfect for a weekend escape or a
              basecamp for exploring southern Colorado.
            </p>

            {/* 🌟 PERSONAL OBSERVATION */}
            <h3>🌟 My Personal Observation</h3>

            <ul>
              {/* GARDEN OF THE GODS */}
              <li>
                <div className="place-block">
                  <strong>Garden of the Gods:</strong> A National Natural Landmark and one of the most dramatic parks in
                  the U.S., featuring towering red sandstone fins and stunning views of Pikes Peak.<br />
                  <a
                    href="https://www.google.com/maps?q=38.865733562087314,-104.89057902165413"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              {/* SEVEN FALLS */}
              <li>
                <div className="place-block">
                  <strong>Seven Falls:</strong> A breathtaking granite box canyon where seven waterfalls cascade 181 feet
                  down the cliff face — especially magical at night when illuminated.<br />
                  <a
                    href="https://www.google.com/maps?q=38.78199615261119,-104.87990815274343"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              {/* PIKES PEAK */}
              <li>
                <div className="place-block">
                  <strong>Pikes Peak:</strong> One of Colorado’s most iconic 14ers, rising 14,115 feet above the plains.
                  The scenic highway offers forests, lakes, and sweeping summit views that inspired “America the
                  Beautiful.”<br />
                  <a
                    href="https://www.google.com/maps?q=38.84053407661129,-105.04483473218366"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>
            </ul>

            {/* 🥾 HIDDEN GEMS */}
            <h3>🥾 Hidden Gems in Colorado Springs</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Helen Hunt Falls:</strong> A peaceful waterfall in North Cheyenne Cañon Park — easy access and
                  beautiful scenery.<br />
                  <a
                    href="https://www.google.com/maps?q=38.78877732314779, -104.90299781229066"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Soaring Adventure Ziplines:</strong> High‑altitude ziplining above Seven Falls — thrilling and
                  scenic.<br />
                  <a
                    href="https://www.google.com/maps?q=38.792340801554914, -104.84696174538452"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Miramont Castle:</strong> A Victorian mansion filled with history and charm in Manitou
                  Springs.<br />
                  <a
                    href="https://www.google.com/maps?q=38.859284211920226, -104.9225201594892"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📍 View on Google Maps
                  </a>
                </div>
              </li>
            </ul>

            {/* 🛏️ PLACES TO STAY */}
            <h3>🛏️ Places to Stay in Colorado Springs</h3>

            <ul>
              <li>
                <div className="place-block">
                  <strong>Hotel Polaris Colorado Springs:</strong> Rated 9.8/10 on Expedia — modern, clean, and highly
                  reviewed.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Garden of the Gods Resort and Club:</strong> Rated 9.4/10 on Expedia — luxury with iconic red
                  rock views.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Holiday Inn Express & Suites South I‑25:</strong> Rated 9.3/10 on Booking.com — reliable and
                  comfortable.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>The Broadmoor:</strong> Rated 9.6/10 by U.S. News Travel — historic, elegant, and world‑class.
                </div>
              </li>

              <li>
                <div className="place-block">
                  <strong>Cliff House at Pikes Peak:</strong> Rated 9.2/10 on U.S. News Travel — charming and historic.
                </div>
              </li>
            </ul>

            {/* AFFILIATE BLOCK */}
           <h3>If you are looking for a place to stay, you can check prices, date and time here.</h3>
                       <div className="affiliate-block">
                         <AffiliateExpedia />
                         <AffiliateBooking />
                         <AffiliateTrip />
                       </div>

            {/* 🎥 VIDEOS */}
            <h3>🎥 My Travel Videos</h3>

            <p>Here are some videos from Colorado Springs.</p>

            <p>
              <a href="https://youtu.be/fIdYDGMK9QE?si=QNbEwXnUAnFqaETd" target="_blank" rel="noopener noreferrer">
                Garden of the Gods
              </a>
              <br />
              <a href="https://youtu.be/-8OoaSPDK-g?si=Ib5QKPLTJCsVV1Ux" target="_blank" rel="noopener noreferrer">
                Seven Falls
              </a>
              <br />
              <a href="https://youtu.be/t1F4_5GSuKk?si=JTO8vKTKLFvgXQIv" target="_blank" rel="noopener noreferrer">
                Helen Hunt Falls
              </a>
              <br />
              <a href="https://youtu.be/x7p5CwpTtxg?si=mc8aGqzgJxq_od1b" target="_blank" rel="noopener noreferrer">
                Pikes Peak
              </a>
              <br />
              <a href="https://youtu.be/jVl-6yYk4EU?si=i3ZZnQAj5YJLg_vs" target="_blank" rel="noopener noreferrer">
                Pikes Peak 360
              </a>
            </p>
          </div>

          {/* RIGHT COLUMN — IMAGES */}
          <div className="travel-image-column">
            <a
              href="https://youtu.be/-8OoaSPDK-g?si=Ib5QKPLTJCsVV1Ux"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img src="/images/usa/colorado/coloradosprings/sevenfalltop.jpg" alt="Seven Falls from Top Video" />
            </a>

            <a
              href="https://youtu.be/x7p5CwpTtxg?si=mc8aGqzgJxq_od1b"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img src="/images/usa/colorado/coloradosprings/train.jpg" alt="Pikes Peak Video" />
            </a>

            <a
              href="https://youtu.be/fIdYDGMK9QE?si=QNbEwXnUAnFqaETd"
              target="_blank"
              rel="noopener noreferrer"
              className="youtube-image-card"
            >
              <img src="/images/usa/colorado/coloradosprings/garden.jpg" alt="Garden of the Gods Video" />
            </a>

            <img src="/images/usa/colorado/coloradosprings/sevenfalls.jpg" alt="Seven Falls" />
            <img src="/images/usa/colorado/coloradosprings/thetunnel.jpg" alt="Tunnel" />
            <img src="/images/usa/colorado/coloradosprings/therestaurant.jpg" alt="Restaurant" />
            <img src="/images/usa/colorado/coloradosprings/thebalancingrock.jpg" alt="Balancing Rock" />
            <img src="/images/usa/colorado/coloradosprings/gofg.jpg" alt="Garden of the Gods Rocks" />
            <img src="/images/usa/colorado/coloradosprings/gotg3.jpg" alt="Garden of the Gods" />
          </div>
        </section>
      </div>

      {/* EMAIL BUTTON */}
      <div className="email-wrapper">
        <button className="email-link" onClick={() => setOpen(true)}>
          contact@thefindingspot.com
        </button>
      </div>

      <ContactPopup isOpen={open} onClose={() => setOpen(false)} />

      <Footer />
    </>
  );
}
